import { IEntityPickerFetcher, EpickTreeConstants } from './entityPickerFetcher';
import { IEpickNode } from './EpickTree';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { EntityTypeInfo, newEntityTypeInfo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { IEntityPickerTreeModel, getTreeNode, defaultEpickTreeModel } from './defaultEpickTreeModel';
import { EntityPickerItem } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityPickerItem';

/**
 * DecisionsTypePickerTreeModel
 */
export function decisionsTypeTreeModel(
  settings: DecisionsTypePickerSettings,
  dataFetcher: IEntityPickerFetcher
): IEntityPickerTreeModel {
  const base = defaultEpickTreeModel(settings, dataFetcher);

  const getSubNodes = async (nodeId: string): Promise<ITreeNode<IEpickNode>[]> => {
    const subs: ITreeNode<IEpickNode>[] = [];
    if (nodeId === EpickTreeConstants.userDefinedTypesFolderId) {
      if (settings.TypeNodesInfo) {
        settings.TypeNodesInfo.forEach((ut: DataPair) => {
          subs.push(
            getTreeNode({
              id: ut.Name,
              name: ut.OutputValue,
              expandable: true,
            } as IEpickNode)
          );
        });
      }
      return subs;
    }    

    if (settings.TypeNodesInfo) {
      const userType: DataPair = settings.TypeNodesInfo.filter((dp: DataPair) => dp.Name === nodeId)[0];
      if (userType) {
        const etype: EntityTypeInfo = newEntityTypeInfo();
        etype.EntityTypeName = userType.Name;
        const entities: EntityPickerItem[] = await dataFetcher.fetchEntitiesOfTypes([etype]);
        if (entities) {
          subs.push(
            ...entities.map(e => {
              const [name, type] = splitNameAndType(e.EntityName);
              return getTreeNode({
                id: e.Id,
                name: name,
                favoriteType: e.FavoriteType,
                pickable: true,
                canBeFav: true,
                title: type,
                entity: e,
              } as IEpickNode)
              }
            )
          );
        }
        return subs;
      }
    }

    const nodes = await base.getSubNodes(nodeId);
    return nodes.map(node => {
      const [name, type] = splitNameAndType(node.data?.name ?? '');
      node.name = name;
      node.description = type;
      return node;
    });
  };

  const getRootNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const roots = await base.getRootNodes();

    if (settings.TypeNodesInfo) {
      roots.push(
        getTreeNode({
          id: EpickTreeConstants.userDefinedTypesFolderId,
          name: EpickTreeConstants.userdefinedTypesFolderName,
          expandable: true,
          order: 7,
        } as IEpickNode)
      );
    }

    for (let rootNode of roots) {
      if (Array.isArray(rootNode.childNodes)) {
        rootNode.childNodes.map(node => {
          const [name, type] = splitNameAndType(node.data?.name ?? '');
          node.name = name;
          node.description = type;
          return node;
        });
      }
    }
    return roots;
  };

  return { ...base, getSubNodes, getRootNodes };
}

export function splitNameAndType(nameString: string): [string, string | undefined] {
  const re = /\[(.*?)\]/g;

  const type = nameString.match(re);
  const name = nameString.replace(re, '').trim();

  return [name, type?.[0]];
}
